import { useEffect, useRef } from 'react';
// Owns mode-strip gestures and focus; the app supplies domain experience order.
export type Experience = 'wall' | 'visual' | 'talk' | 'circles';
export interface ModeWheelProps {
  active: Experience;
  available: readonly Experience[];
  labels: Record<Experience, string>;
  onSelect: (value: Experience) => void;
  renderIcon: (value: Experience) => SVGSVGElement;
}
/** Compact mode strip. Explicit taps and arrow-key selection share one callback. */
export function ModeWheel({ active, available, labels, onSelect, renderIcon }: ModeWheelProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const reveal = () => {
      const button = track.querySelector<HTMLButtonElement>(`[data-experience="${active}"]`);
      if (!button) return;
      const left = button.offsetLeft;
      const right = left + button.offsetWidth;
      if (left < track.scrollLeft) track.scrollLeft = left;
      else if (right > track.scrollLeft + track.clientWidth)
        track.scrollLeft = right - track.clientWidth;
    };
    const frame = requestAnimationFrame(reveal);
    const observer = new ResizeObserver(reveal);
    observer.observe(track);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active]);
  return (
    <nav className="mode-wheel" aria-label="Interface modes">
      <div className="mode-wheel-track" ref={trackRef}>
        {available.map((value) => (
          <button
            type="button"
            key={value}
            className="mode-wheel-option"
            data-experience={value}
            data-focus-key={`experience-${value}`}
            aria-pressed={value === active}
            aria-label={labels[value]}
            title={labels[value]}
            onClick={(event) => {
              event.currentTarget.focus();
              onSelect(value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                const index = available.indexOf(value);
                const next =
                  available[
                    Math.max(
                      0,
                      Math.min(available.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)),
                    )
                  ];
                if (next) {
                  trackRef.current
                    ?.querySelector<HTMLButtonElement>(`[data-experience="${next}"]`)
                    ?.focus({ preventScroll: true });
                  onSelect(next);
                }
              }
            }}
          >
            <span
              aria-hidden="true"
              ref={(host) => {
                if (host) host.replaceChildren(renderIcon(value));
              }}
            />
            <span
              className={`mode-label${Array.from(labels[value]).length > 8 ? ' mode-label-long' : ''}`}
              data-short-label={Array.from(labels[value]).slice(0, 3).join('')}
              aria-hidden="true"
            >
              {labels[value]}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}
